import type { EnvRouteKey } from "../appRoutes/appRoutes";
import { envRoutes } from "../appRoutes/appRoutes";
import {
  computeRedirectByNameAndPitPath,
  pathParamsSchema,
  taskRedirectByNameAndPitPath,
  taskRedirectPath,
  transformRedirectPath,
} from "../appRoutes/appRoutes.schemas";
import type { ExactPathParam, PathParams } from "../appRoutes/appRoutes.types";

// Filters to routes with a schema (required for buildLink validation), then sorts
// longest path first so the most-specific route matches before shorter prefixes.
const linkRoutes = Object.values(envRoutes)
  .filter(
    (r): r is typeof r & { schema: NonNullable<(typeof r)["schema"]> } =>
      r.schema != null
  )
  .sort((a, b) => b.path.split("/").length - a.path.split("/").length);

export const buildLink = <T extends PathParams>(
  env_name: string,
  pathParams: ExactPathParam<T> | EnvRouteKey
): string | undefined => {
  if (typeof pathParams === "string") {
    const route = envRoutes[pathParams];
    return route ? route.getBasePath(env_name) : undefined;
  }

  // Inject env_name; the first-arg value takes precedence over any env_name in pathParams
  const fullParams = { ...pathParams, env_name };

  const anyParsed = pathParamsSchema.safeParse(fullParams);

  let search =
    anyParsed.success && new URLSearchParams(anyParsed.data.search).toString();
  search = search ? `?${search}` : "";

  const parsedHash = anyParsed.data?.hash;

  let hash =
    anyParsed.success &&
    new URLSearchParams(
      parsedHash
        ? typeof parsedHash === "string"
          ? parsedHash.replace?.(/^#/, "")
          : parsedHash
        : anyParsed.data.search?.hash?.replace?.(/^#/, "")
    ).toString();
  hash = hash ? `#${hash}` : "";

  if (!anyParsed.success) {
    console.error(`Failed parsing path params for link.`, fullParams);
    return undefined;
  }

  const getRedirectionURL = (str: string) =>
    `/${str}${search}${hash}`.replace(/\/+/g, "/");

  // TODO consider adding an error when some params are missing. Solution might be a schema or.
  // Order matters: more-specific schemas (those with redirect:true or extra required fields)
  // must come before generic ones since Zod accepts extra keys.

  // Redirect-only routes — no corresponding appRoutes entry, handled manually.
  const redirectRoutes = [
    {
      getURL: (p: any) =>
        getRedirectionURL(
          `compute/${p.env_name}/${p.app_name}/${p.cluster_name}/${p.run_time}`
        ),
      safeParse: computeRedirectByNameAndPitPath.safeParse(fullParams),
    },
    {
      getURL: (p: any) => {
        let taskSearch = search;
        if (p.status) {
          const params = new URLSearchParams(
            anyParsed.success ? anyParsed.data.search : undefined
          );
          params.set("status", p.status);
          const qs = params.toString();
          taskSearch = qs ? `?${qs}` : "";
        }
        return `/tasks/${p.env_name}/${p.app_name}/${p.task_name}/${p.app_pit}${taskSearch}${hash}`.replace(
          /\/+/g,
          "/"
        );
      },
      safeParse: taskRedirectByNameAndPitPath.safeParse(fullParams),
    },
    {
      getURL: (p: any) => getRedirectionURL(`transforms/${p.tf_id}`),
      safeParse: transformRedirectPath.safeParse(fullParams),
    },
    {
      getURL: (p: any) => getRedirectionURL(`tasks/${p.task_id}`),
      safeParse: taskRedirectPath.safeParse(fullParams),
    },
  ];

  for (const route of redirectRoutes) {
    if (route.safeParse.success) {
      return route.getURL(route.safeParse.data);
    }
  }

  // Routes derived from appRoutes entries that have schema + linkPriority,
  // sorted ascending by linkPriority (most-specific first).
  for (const route of linkRoutes) {
    const parsed = route.schema.safeParse(fullParams);
    if (parsed.success) {
      const mappedParams = route.buildLinkParamMapper
        ? {
            env_name,
            ...route.buildLinkParamMapper(parsed.data),
          }
        : parsed.data;
      // biome-ignore lint/suspicious/noExplicitAny: mappedParams is any
      return `${route.getPath(mappedParams as any)}${search}${hash}`;
    }
  }

  console.error("No matching route found for path params", fullParams);
  return undefined;
};
